/**
 * Yatzy Game Page Orchestrator (TypeScript)
 * 
 * This orchestrator imports all modular components and wires them together:
 * - dice-renderer: 3D/2D dice rendering
 * - scorecard-renderer: Scorecard UI and ruleset management
 * - auth-state: Authentication and user data loading
 * - game-actions: Core game logic (start, roll, hold, score)
 * - modals: Modal dialogs and guest tracking
 * - keyboard-shortcuts: Desktop keyboard shortcuts
 * - shake-detection: Mobile shake detection
 * 
 * Note: Multiplayer matches use /match/{id} route, not this page.
 */

// External dependencies
import { querySelector, setText } from '../lib/dom-utils';
import { showToast } from '../lib/toast';
import { logger } from '../lib/logger';
import { showResumeBanner } from '../lib/resume-banner';
import type { AnimationTier } from '../lib/animation-pref';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { showEndOfGameOverlay, buildSoloWinConfig, type NextGameSuggestions } from '../components/end-of-game-overlay';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { celebrationEffect, delayEffect, EOG_BUFFER_MS } from '../lib/animation-effects';
import type { ApiResponseWithCelebrations } from '../utils/celebration-handler';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';

// Yatzy modules
import { renderDice } from './yatzy/dice-renderer';
import { 
  renderScorecard, 
  setRulesetDefinitions, 
  getRulesetDefinition 
} from './yatzy/scorecard-renderer';
import {
  loadAuthState,
  loadUserData,
  loadRulesets
} from './yatzy/auth-state';
import {
  updateMeta as updateGameMeta,
  startGame as startGameAction,
  roll as rollAction,
  toggleHold as toggleHoldAction,
  scoreCategory as scoreCategoryAction,
  UpdateMetaParams,
  StartGameParams,
  RollParams
} from './yatzy/game-actions';
import {
  cleanupLegacyGuestKeys
} from './yatzy/modals';
import {
  enableKeyboardShortcuts
} from './yatzy/keyboard-shortcuts';
import {
  initShakeDetection,
  updateRollButtonText,
  handleEnableShake,
  handleDeclineShake
} from './yatzy/shake-detection';

// =============================================================================
// Type Definitions
// =============================================================================

interface GameState {
  id: number;
  current_roll: number;
  rolls_left?: number;
  total_score: number;
  game_state: string;
  ruleset_id?: string;
  dice_values?: number[];
  dice_held?: boolean[];
  match_id?: string;
  [key: string]: any;
}

interface AuthStateData {
  authenticated: boolean;
  [key: string]: any;
}

interface UserDataType {
  preferences?: {
    yatzy_ruleset?: string;
    animations?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// Bootstrap Modal type
declare const bootstrap: {
  Tooltip: new (element: HTMLElement) => any;
};

// =============================================================================
// State Management
// =============================================================================

// Game state
let game: GameState | null = null;
let holdMask: boolean[] = [false, false, false, false, false];
let animationLevel: AnimationTier = 'playful';

// Shake detection state
let shakeDetector: any = null;
let isShaking: boolean = false;
let currentShakeDuration: number = 0;
let shakeReadyTimeout: number | null = null;
let shakeEnabled: boolean = true;
let isRolling: boolean = false;

// User state
let userData: UserDataType | null = null;
let selectedRulesetId: string = 'yatzy_standard_us';
let authState: AuthStateData = { authenticated: false };

// Keyboard state
let keyboardCleanup: (() => void) | null = null;

// Shake detection cleanup
let shakeCleanup: (() => void) | null = null;

// Entry context from URL param — consumed once on first start
let pendingEntryContext: string | undefined;

// End-of-game orchestrator handle for cancellation
let eogHandle: SequenceHandle | undefined;

// DOM elements (cached)
let diceGrid: HTMLElement | null;
let scoreBodyMobile: HTMLElement | null;
let scoreBodyLeft: HTMLElement | null;
let scoreBodyRight: HTMLElement | null;
let rollBtn: HTMLButtonElement | null;
let rollCountEl: HTMLElement | null;
let totalScoreElements: NodeListOf<Element> | null;
let gameStateLabel: HTMLElement | null;
let startBtn: HTMLElement | null;
let selectionSection: HTMLElement | null;
let gameArea: HTMLElement | null;
let newGameBtn: HTMLElement | null;
let rulesetCardsContainer: HTMLElement | null;
let motionPromptEl: HTMLElement | null;

// Initialization guard
let isInitialized: boolean = false;

// =============================================================================
// Helper Functions
// =============================================================================

function setStatus(msg: string): void {
  if (gameStateLabel) {
    gameStateLabel.textContent = msg;
    gameStateLabel.style.display = msg ? 'inline' : 'none';
  }
}

function logError(message: string, err: any): void {
  logger.error(message, err);
  setStatus('Error');
}

function showCenteredInfoBadge(text: string, duration: number = 2000): void {
  const badge = document.createElement('div');
  badge.className = 'centered-info-badge';
  badge.textContent = text;
  document.body.appendChild(badge);
  
  setTimeout(() => {
    badge.classList.add('fade-out');
    setTimeout(() => badge.remove(), 400);
  }, duration);
}

// =============================================================================
// Wrapped Module Functions (with state management)
// =============================================================================

/**
 * Update game metadata UI
 */
function updateMeta(): void {
  updateGameMeta({
    game,
    rollCountEl,
    totalScoreElements,
    gameStateLabel,
    startBtn,
    rollBtn
  });
}

/**
 * Start a new game
 */
// ---------------------------------------------------------------------------
// Resume Active Game
// ---------------------------------------------------------------------------

async function checkActiveGame(): Promise<boolean> {
  try {
    const resp = await fetch('/api/games/yatzy/active', { credentials: 'same-origin' });
    if (!resp.ok) return false;
    const data = await resp.json();
    if (!data.game) return false;

    // Resume the active game
    game = data.game;
    selectedRulesetId = data.ruleset_id || data.game.ruleset_id || 'yatzy_standard_us';
    holdMask = data.game.dice_held || [false, false, false, false, false];

    hideRulesetSelection();
    renderAllUI();
    updateMeta();

    // Show resume banner
    if (gameArea) {
      showResumeBanner({
        container: gameArea,
        onAbandon: async () => {
          try {
            await fetch('/api/games/yatzy/abandon', { method: 'POST' });
          } catch (err) {
            logger.error('Failed to abandon game:', err);
          }
          game = null;
          holdMask = [false, false, false, false, false];
          showRulesetSelection();
        },
      });
    }

    logger.info('Resumed active Yatzy game');
    return true;
  } catch (err) {
    logger.warn('Failed to check active Yatzy game', err);
    return false;
  }
}

// =============================================================================
// Game Actions
// =============================================================================

async function startGame(rulesetId?: string): Promise<GameState | null> {
  // Cancel any pending end-of-game sequence
  eogHandle?.cancel();
  eogHandle = undefined;

  // Consume entry context once (from URL param on first start)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  const newGame = await startGameAction({
    rulesetId: rulesetId || selectedRulesetId,
    selectedRulesetId,
    authState,
    userData,
    matchId: null,  // Solo play only - matches use /match/{id} route
    entryContext: ec,
    setStatus,
    logError,
    startBtn: startBtn as HTMLButtonElement | null
  });
  
  // Update state
  if (newGame) {
    game = newGame;
    // Restore held dice state from resumed game (for single player persistence)
    holdMask = newGame.dice_held || [false, false, false, false, false];
    if (rulesetId) {
      selectedRulesetId = rulesetId;
    }
  }
  
  // Re-render
  renderAllUI();
  updateMeta();
  
  window.__versionGuard?.setSafeToReload(false);
  return newGame;
}

/**
 * Roll the dice
 */
async function roll(durationOverride?: number): Promise<void> {
  // Prevent double-roll
  if (isRolling) {
    logger.log('Already rolling, skipping');
    return;
  }
  
  // Disable shake during roll
  const wasShakeEnabled = shakeEnabled;
  shakeEnabled = false;
  isRolling = true;
  
  try {
    const result = await rollAction({
      game,
      holdMask,
      animationLevel,
      authState,
      isRolling: false,
      shakeEnabled,
      shakeReadyTimeout,
      shakeDetector,
      rollBtn: rollBtn!,
      setStatus,
      logError,
      updateRollButtonText: () => updateRollButtonText({ rollBtn }),
      startGameFn: () => startGame()
    }, durationOverride);
    
    // Update state
    if (result.game) {
      game = result.game;
      holdMask = result.holdMask;
      shakeReadyTimeout = result.shakeReadyTimeout;
    }
    
    // Re-render
    renderAllUI();
    updateMeta();
    
  } finally {
    isRolling = false;
    
    // Update button text using cached capability (no flash)
    if (rollBtn) updateRollButtonText({ rollBtn });
    
    // Re-enable shake after cooldown (no button text change needed - already correct)
    if (wasShakeEnabled && shakeDetector) {
      const cooldown = 1000;
      shakeReadyTimeout = window.setTimeout(() => {
        shakeEnabled = true;
      }, cooldown);
    }
  }
}

/**
 * Toggle hold state on a die
 */
async function toggleHold(idx: number): Promise<void> {
  // Prevent toggling during roll
  if (isRolling) {
    logger.log('Cannot toggle hold while rolling');
    return;
  }
  
  const result = await toggleHoldAction({
    game,
    holdMask,
    idx,
    setStatus,
    logError
  });
  
  holdMask = result;
  renderAllUI();
}

/**
 * Score a category
 */
async function scoreCategory(category: string): Promise<void> {
  const result = await scoreCategoryAction({
    game,
    holdMask,
    category,
    setStatus,
    logError,
    showCenteredInfoBadge
  });
  
  if (result.game) {
    game = result.game;
    holdMask = result.holdMask;
  }
  
  renderAllUI();
  updateMeta();

  // Show end-of-game overlay when all categories are scored
  if (game && game.game_state === 'complete') {
    showYatzyEndOfGame(result.apiResponse ?? {}, result.fpEarned, result.hypotheticalFP, result.playEventId, result.nextGameSuggestions as NextGameSuggestions | undefined);
  }
}

/**
 * Show the end-of-game overlay for a completed Yatzy game.
 * Uses the animation orchestrator to sequence celebration → buffer → modal.
 */
function showYatzyEndOfGame(apiResponse: ApiResponseWithCelebrations, fpEarned?: number, _hypotheticalFP?: number, playEventId?: string, nextGameSuggestions?: NextGameSuggestions): void {
  if (!game) return;
  void loadStats();
  const totalScore = game.total_score ?? 0;
  const isGuest = isGuestUser(authState);

  const config = buildSoloWinConfig({
    game: 'yatzy',
    totalScore,
    fpEarned,
    isGuest,
    onPlayAgain: () => {
      void fireGameCompleted(isGuest);
      startGame();
    },
    playAgainUrl: '/games/yatzy',
    playEventId,
    nextGameSuggestions,
  });

  // Cancel any pending end-of-game sequence (e.g. rapid play-again)
  eogHandle?.cancel();

  // Orchestrated sequence: celebration → buffer → modal
  eogHandle = runSequence('yatzy-eog', [
    celebrationEffect(apiResponse),
    delayEffect(EOG_BUFFER_MS),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
  });
}

/**
 * Render all UI components
 */
function renderAllUI(): void {
  // Render dice
  renderDice({
    diceGrid: diceGrid!,
    game,
    holdMask,
    animationLevel,
    onToggleHold: toggleHold
  });
  
  // Render scorecard
  renderScorecard({
    scoreBodyMobile: scoreBodyMobile!,
    scoreBodyLeft: scoreBodyLeft!,
    scoreBodyRight: scoreBodyRight!,
    game,
    selectedRulesetId: game?.ruleset_id || selectedRulesetId,
    onScoreCategory: scoreCategory
  });
  
  // Re-cache total score elements after scorecard render
  totalScoreElements = document.querySelectorAll('.total-score, .total-score-count');
}

// =============================================================================
// Ruleset Selection (Inline Cards)
// =============================================================================

interface RulesetData {
  ruleset_id: string;
  name: string;
  description?: string;
  is_active?: boolean;
}

/** Cached rulesets for re-rendering the selection section */
let cachedRulesets: RulesetData[] = [];

/**
 * Render ruleset gc-cards into the inline selection section
 */
function renderRulesetCards(rulesets: RulesetData[]): void {
  if (!rulesetCardsContainer) return;
  cachedRulesets = rulesets;

  const activeRulesets = rulesets.filter(r => r.is_active !== false);

  rulesetCardsContainer.innerHTML = activeRulesets.map(r => `
    <div class="card gc-card gc-card-vertical${r.ruleset_id === selectedRulesetId ? ' gc-card-selected' : ''}"
         data-ruleset-id="${r.ruleset_id}" data-testid="ruleset-${r.ruleset_id}">
      <div class="card-body d-flex justify-content-between align-items-center">
        <div>
          <div class="gc-card-label">${r.name}</div>
          ${r.description ? `<div class="gc-card-tagline">${r.description}</div>` : ''}
        </div>
      </div>
    </div>
  `).join('');

  rulesetCardsContainer.querySelectorAll('.gc-card').forEach(card => {
    card.addEventListener('click', () => {
      const id = (card as HTMLElement).dataset.rulesetId;
      if (id) {
        selectedRulesetId = id;
        hideRulesetSelection();
        startGame(id);
      }
    });
  });
}

/**
 * Show the inline ruleset selection section, hide the game area
 */
function showRulesetSelection(): void {
  window.__versionGuard?.setSafeToReload(true);
  if (!selectionSection || !gameArea) return;
  // Re-render cards with latest selected state
  if (cachedRulesets.length > 0) {
    renderRulesetCards(cachedRulesets);
  }
  selectionSection.style.display = '';
  gameArea.style.display = 'none';
}

/**
 * Hide the selection section and show the game area
 */
function hideRulesetSelection(): void {
  if (!selectionSection || !gameArea) return;
  selectionSection.style.display = 'none';
  gameArea.style.display = '';
}

// =============================================================================
// DOM Setup
// =============================================================================

function cacheDom(): void {
  diceGrid = querySelector('#diceGrid');
  scoreBodyMobile = querySelector('#scoreBodyMobile');
  scoreBodyLeft = querySelector('#scoreBodyLeft');
  scoreBodyRight = querySelector('#scoreBodyRight');
  rollBtn = querySelector('#rollBtn') as HTMLButtonElement;
  rollCountEl = querySelector('#rollCount');
  totalScoreElements = document.querySelectorAll('.total-score, .total-score-count');
  gameStateLabel = querySelector('#gameStateLabel');
  startBtn = querySelector('#startGameBtn');
  selectionSection = querySelector('#selectionSection');
  gameArea = querySelector('#gameArea');
  newGameBtn = querySelector('#newGameBtn');
  rulesetCardsContainer = querySelector('#rulesetCards');
  motionPromptEl = querySelector('#motionPermissionPrompt');
}

function bindEvents(): void {
  if (startBtn) {
    startBtn.addEventListener('click', () => startGame());
  }
  
  if (newGameBtn) {
    newGameBtn.addEventListener('click', () => showRulesetSelection());
  }
  
  if (rollBtn) {
    rollBtn.addEventListener('click', () => roll());
  }
}

// =============================================================================
// Animations Modal (shared component)
// =============================================================================

function initYatzyAnimationsModal(): void {
  initAnimationsModal({
    gamePrefix: 'yatzy',
    cssClassPrefix: 'yatzy-anim',
    pageElementId: 'yatzyPage',
    onTierChange: (tier) => {
      const prevLevel = animationLevel;
      const newLevel = tier as AnimationTier;
      animationLevel = newLevel;

      const needsRerender = (prevLevel === 'playful' || newLevel === 'playful') && prevLevel !== newLevel;
      if (needsRerender) {
        renderAllUI();
      }
    },
  });
}

// =============================================================================
// Shake Detection Integration
// =============================================================================

function initShakeDetectionIntegration(): void {
  const { shakeDetector: detector, cleanup } = initShakeDetection({
    onPermissionGranted: () => {
      shakeDetector = detector;
    },
    onShakeStart: () => {
      if (!shakeEnabled || isRolling || !game || game.rolls_left === 0) {
        return;
      }
      
      isShaking = true;
      currentShakeDuration = 0;
      
      if (rollBtn) {
        rollBtn.disabled = true;
      }
    },
    onShakeActive: (duration: number) => {
      if (isRolling || !game || game.rolls_left === 0) return;
      currentShakeDuration = duration;
    },
    onShakeEnd: (duration: number) => {
      if (isRolling || !game || game.rolls_left === 0) {
        isShaking = false;
        return;
      }
      
      isShaking = false;
      const animationDuration = Math.max(1200, duration * 1.5);
      roll(animationDuration);
    },
    rollBtn,
    motionPromptEl
  });
  
  shakeDetector = detector;
  shakeCleanup = cleanup;
}

// =============================================================================
// Keyboard Shortcuts Integration
// =============================================================================

function initKeyboardShortcutsIntegration(): void {
  keyboardCleanup = enableKeyboardShortcuts({
    onRoll: () => roll(),
    onToggleHold: (idx: number) => toggleHold(idx),
    isRollDisabled: () => {
      return rollBtn === null || rollBtn.disabled || isRolling || game?.game_state === 'complete';
    }
  }) || null;
}

// =============================================================================
// Initialization
// =============================================================================

async function init(): Promise<void> {
  if (isInitialized) {
    logger.log('Already initialized, skipping');
    return;
  }
  isInitialized = true;
  
  // Setup DOM
  cacheDom();
  bindEvents();
  
  // Check for ruleset override in URL parameters (for E2E tests and direct links)
  const urlParams = new URLSearchParams(window.location.search);
  const rulesetParam = urlParams.get('ruleset') || urlParams.get('difficulty');
  const isAutoStart = !!rulesetParam;  // any value (including 'auto') triggers auto-start
  const entryContext = urlParams.get('ec') || undefined;
  if (rulesetParam && rulesetParam !== 'auto') {
    selectedRulesetId = rulesetParam;
    logger.log(`Ruleset override from URL parameter: ${rulesetParam}`);
  }
  if (entryContext) {
    pendingEntryContext = entryContext;
  }
  // Clean ?ec= and ?difficulty= from URL to avoid re-triggering on refresh
  if (entryContext || urlParams.has('difficulty')) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('ec');
    cleanUrl.searchParams.delete('difficulty');
    history.replaceState(null, '', cleanUrl.toString());
  }
  
  // Load configuration from template
  const pageEl = document.querySelector('.yatzy-page') as HTMLElement;
  if (pageEl) {
    // Only use saved preference if no URL parameter was provided
    if (!rulesetParam && pageEl.dataset.savedRulesetId) {
      selectedRulesetId = pageEl.dataset.savedRulesetId;
    }
  }
  
  // Clean up legacy guest tracking keys
  cleanupLegacyGuestKeys();
  
  // Load rulesets and render inline selection cards
  const rulesets = await loadRulesets({ hydrate: true });
  if (!getRulesetDefinition(selectedRulesetId)) {
    selectedRulesetId = 'yatzy_standard_us';
  }
  renderRulesetCards(rulesets);
  
  // Initialize Bootstrap tooltips
  const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl as HTMLElement));
  
  // Initial render
  renderAllUI();
  updateMeta();
  
  // Load auth state
  const authResult = await loadAuthState();
  authState = authResult.authState;
  
  // Load user data for authenticated users
  if (authResult.authenticated) {
    const userDataResult = await loadUserData({ authState, animationLevel, selectedRulesetId });
    userData = userDataResult.userData;
    animationLevel = userDataResult.animationLevel;
    // Only use saved preference if no URL parameter was provided
    // URL parameter takes precedence for deep links and E2E tests
    if (!rulesetParam) {
      selectedRulesetId = userDataResult.selectedRulesetId;
    }
  }
  
  // Note: Multiplayer matches use /match/{id} route with embedded game
  // This page is for solo play only
  
  // Initialize keyboard shortcuts (desktop only)
  initKeyboardShortcutsIntegration();
  
  // Initialize shake detection (mobile only)
  initShakeDetectionIntegration();
  
  // Initialize celebration particle system (async, non-blocking)
  safeInitCelebrations();
  
  // If URL has a ruleset/difficulty param (deep link / E2E), skip selection and start immediately
  // 'auto' means use saved preference (selectedRulesetId already set from user data/template)
  if (isAutoStart) {
    hideRulesetSelection();
    startGame();
  } else {
    // Check for active game to resume (solo only — matches use /match/<id>)
    await checkActiveGame();
    void loadStats();
  }
  // Otherwise, selection section is already visible by default — user picks a ruleset card
}

async function loadStats(): Promise<void> {
  const statsCard = document.getElementById('statsCard');
  if (!statsCard) return;

  // Show guest placeholder if not authenticated
  if (isGuestUser(authState)) {
    const cardBody = statsCard.querySelector('.card-body');
    if (cardBody) {
      cardBody.innerHTML = `
        <h6 class="text-muted mb-3">Your Stats</h6>
        <div class="text-center py-3" data-testid="guest-stats-placeholder">
          <p class="mb-1 text-muted">Your game stats will appear here</p>
          <p class="small text-muted mb-2">Available when signed in</p>
          <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
            Sign in to start tracking →
          </a>
        </div>`;
      const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
      if (signinLink) {
        signinLink.addEventListener('click', (e) => {
          e.preventDefault();
          void fireGuestEvent('tried_locked_feature', true).then((shown) => {
            if (!shown) {
              ensureAuthModalExists();
              authModal.show();
            }
          });
        });
      }
    }
    return;
  }

  try {
    const resp = await fetch('/api/games/yatzy/stats');
    if (!resp.ok) return;
    const data = await resp.json() as { high_score: number; games_played: number; avg_score: number };
    const highScore = document.getElementById('statHighScore');
    const games = document.getElementById('statGames');
    const avgScore = document.getElementById('statAvgScore');
    if (highScore) highScore.textContent = String(data.high_score);
    if (games) games.textContent = String(data.games_played);
    if (avgScore) avgScore.textContent = String(data.avg_score);
  } catch (err) {
    logger.warn('[Yatzy] Failed to load stats', err);
  }
}

async function initWhenReady(): Promise<void> {
  await init();
  initYatzyAnimationsModal();
  
  // Wire up shake permission buttons
  const enableBtn = querySelector('#enableShakeBtn');
  const declineBtn = querySelector('#declineShakeBtn');
  const shakeAlertClose = querySelector('#shakeAlertClose');
  
  if (enableBtn) {
    enableBtn.addEventListener('click', () => {
      handleEnableShake({
        onPermissionGranted: () => {
          if (!shakeDetector) {
            initShakeDetectionIntegration();
          }
        },
        motionPromptEl,
        rollBtn
      });
    });
  }
  
  if (declineBtn) {
    declineBtn.addEventListener('click', () => {
      handleDeclineShake({ motionPromptEl });
    });
  }
  
  if (shakeAlertClose) {
    shakeAlertClose.addEventListener('click', () => {
      if (declineBtn) (declineBtn as HTMLElement).click();
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhenReady);
} else {
  initWhenReady();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (shakeCleanup) shakeCleanup();
  if (keyboardCleanup) keyboardCleanup();
});

// Handle bfcache restore — fires when browser restores this page from the
// Back-Forward Cache after navigating away and back (or cross-game navigation).
// DOMContentLoaded does NOT fire on bfcache restore, so module-level state
// (isRolling, disabled rollBtn) can be frozen at mid-roll or end-of-turn values.
window.addEventListener('pageshow', async (event) => {
  if (!event.persisted) return; // Only handle bfcache restores
  logger.info('[Yatzy] Page restored from bfcache — refreshing game state');

  // Reset stale in-flight flag (frozen if user navigated away mid-roll)
  isRolling = false;

  // Re-fetch authoritative game state from the server so the roll button
  // reflects the correct current_roll count rather than the stale frozen value.
  const resumed = await checkActiveGame();
  if (!resumed) {
    // No active game — or it completed while away — show fresh selection screen
    game = null;
    showRulesetSelection();
    updateMeta();
  }
});
